"use client";

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Sun, Sparkles, Camera, Fingerprint, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from '@/components/common/Link';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function LAlchimiaDellaStampa() {
  const [isDeveloping, setIsDeveloping] = useState(false);
  const [developmentProgress, setDevelopmentProgress] = useState(0);
  const requestRef = useRef<number>(0);
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll();
  const animateDevelopment = useCallback(() => {
    if (isDeveloping) {
      setDevelopmentProgress(prev => {
        const next = prev + 0.5;
        return next >= 100 ? 100 : next;
      });
      requestRef.current = requestAnimationFrame(animateDevelopment);
    } else {
      setDevelopmentProgress(prev => {
        const next = prev - 1;
        return next <= 0 ? 0 : next;
      });
      if (developmentProgress > 0) {
        requestRef.current = requestAnimationFrame(animateDevelopment);
      }
    }
  }, [isDeveloping, developmentProgress]);
  useEffect(() => {
    requestRef.current = requestAnimationFrame(animateDevelopment);
    return () => cancelAnimationFrame(requestRef.current);
  }, [isDeveloping, animateDevelopment]);
  const handleMouseDown = () => setIsDeveloping(true);
  const handleMouseUp = () => setIsDeveloping(false);
  const handleTouchStart = () => setIsDeveloping(true);
  const handleTouchEnd = () => setIsDeveloping(false);
  return <section id="lalchimia-della-stampa" className="dark bg-background text-foreground relative w-full overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[90vh]">
        
        {}
        <div className="relative h-[50vh] lg:h-auto bg-neutral-950 overflow-hidden group border-b lg:border-b-0 lg:border-r border-border/20">
          {}
          <div className="absolute inset-0 z-20 pointer-events-none transition-opacity duration-700 opacity-0 group-hover:opacity-100 bg-[radial-gradient(circle_at_center,var(--color-primary)_0%,transparent_70%)] mix-blend-overlay opacity-30" />
          
          {}
          <div className="absolute inset-0 z-0 opacity-60 mix-blend-screen overflow-hidden">
            <div style={{
            transform: 'scale(1.5)',
            width: '100%',
            height: '100%'
          }}>
              <iframe src="https://www.youtube.com/embed/qC0vDKVPCrw?autoplay=1&mute=1&loop=1&playlist=qC0vDKVPCrw&controls=0&showinfo=0&modestbranding=1&playsinline=1&disablekb=1&rel=0&iv_load_policy=3" width="100%" height="100%" frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              minWidth: '100%',
              minHeight: '100%',
              width: 'auto',
              height: 'auto'
            }} />
            </div>
          </div>

          {}
          <div className="absolute inset-0 z-10 flex items-center justify-center p-8">
            <div className="relative w-full max-w-md aspect-[4/5] bg-white/5 shadow-2xl backdrop-blur-sm border border-white/10 p-4 transform transition-transform duration-700 group-hover:scale-105">
              {}
              <div className="absolute inset-4 bg-white shadow-inner overflow-hidden">
                {}
                <div className="absolute inset-0 bg-cover bg-center transition-all duration-100 ease-linear" style={{
                backgroundImage: 'url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562462/img/vintage_portrait_sample.png)',
                opacity: developmentProgress / 100,
                filter: `sepia(${1 - developmentProgress / 200}) contrast(${0.8 + developmentProgress / 200}) grayscale(${1 - developmentProgress / 150})`
              }} />
                
                {}
                <div className="absolute inset-0 bg-amber-900/20 mix-blend-multiply pointer-events-none" style={{
                opacity: isDeveloping ? 0.4 : 0
              }} />
              </div>

              {}
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-12 h-4 bg-neutral-800 rounded-sm shadow-lg z-20" />
            </div>
          </div>

          {}
          <div className="absolute bottom-8 left-8 z-30 flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${isDeveloping ? 'bg-red-500 animate-pulse' : 'bg-neutral-600'}`} />
            <span className="text-xs font-mono tracking-widest uppercase text-white/60">
              {isDeveloping ? 'Developing...' : 'Darkroom Standby'}
            </span>
          </div>
        </div>

        {}
        <div className="relative h-auto lg:h-auto bg-background flex flex-col justify-center p-8 lg:p-20">
          <div ref={textRef} className={`max-w-xl mx-auto flex flex-col gap-10 transition-all duration-1000 ${textVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {}
            <div className="order-0 space-y-4">
              <div className="flex items-center gap-2 text-primary font-mono text-sm tracking-widest uppercase">
                <Sparkles className="w-4 h-4" />
                <span>Artisanal Process</span>
              </div>
              <h2 className="text-4xl lg:text-6xl font-serif font-light leading-tight text-foreground">The Alchemy of the Print</h2>
            </div>

            {}
            <div className="order-2 sm:order-1 space-y-6 text-lg text-muted-foreground font-light leading-relaxed">
              <p>In an instant digital world, we slow down time. We use sunlight and traditional chemistry to bring to life portraits that aren't just files, but unique physical objects.</p>
              <p>Every shot is a shared experience: from posing before our handcrafted cameras to the magical moment when the image slowly emerges on paper, right before your eyes.</p>
            </div>

            {}
            <div className="order-first sm:order-2 pt-8 border-t border-border">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="relative group">
                  <div className={`absolute -inset-1 bg-gradient-to-r from-primary to-amber-600 rounded-lg blur opacity-20 group-hover:opacity-40 transition duration-1000 ${isDeveloping ? 'opacity-60 duration-200' : ''}`}></div>
                  <Button size="lg" className="relative min-w-[200px] h-14 text-base font-medium tracking-wide transition-all active:scale-95 select-none" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>Long Press to Develop</Button>
                </div>
                
                <p className="text-xs text-muted-foreground italic block md:hidden text-center">
                  *Prova l'esperienza digitale: tieni premuto il pulsante per simulare lo sviluppo in camera oscura.
                </p>

                <div className="flex-1 w-full sm:w-auto">
                  <div className="flex justify-between text-xs font-mono text-muted-foreground mb-2">
                    <span>Exposure</span>
                    <span>{Math.round(developmentProgress)}%</span>
                  </div>
                  <div className="h-1 w-full bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-all duration-100 ease-out" style={{
                    width: `${developmentProgress}%`
                  }} />
                  </div>
                </div>
              </div>
              <p className="mt-4 text-xs text-muted-foreground italic hidden md:block">*Try the digital experience: long press the button to simulate the darkroom development.</p>
            </div>

            {}
            <div className="order-3 pt-8">
               <Link to="/WeddingsPage">
                <Button variant="link" className="p-0 h-auto text-foreground hover:text-primary transition-colors group">
                  Scopri i nostri servizi per matrimoni
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
               </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}